import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';

/**
 * Manages per-user column visibility for a MUI DataGrid, persisted in localStorage.
 * Compatible with MUI `<DataGrid>` / `<DataGridPro>` (v8+). Wire up the return values directly:
 *
 * ```tsx
 * const { columnVisibilityModel, onColumnVisibilityModelChange } = useHiddenColumns({ ... });
 *
 * <DataGrid
 *   columnVisibilityModel={columnVisibilityModel}
 *   onColumnVisibilityModelChange={onColumnVisibilityModelChange}
 * />
 * ```
 *
 * Storage format: JSON array of hidden field names, e.g. `["sequenceName"]`.
 * Missing entry = first load; apply defaultHiddenColumns.
 * Empty array = user explicitly made all columns visible.
 */

export interface UseHiddenColumnsParams {
  storageKey: string;
  allColumns: GridColDef[];
  visibleColumns: GridColDef[];
  defaultHiddenColumns?: string[];
}

export interface UseHiddenColumnsResult {
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
}

export function useHiddenColumns(params: UseHiddenColumnsParams): UseHiddenColumnsResult {
  const { storageKey, allColumns, visibleColumns } = params;

  const allColumnFieldNames = useMemo(() => new Set(allColumns.map((c) => c.field)), [allColumns]);

  const [hiddenFieldNames, setHiddenFieldNames] = useState<Set<string>>(() => {
    const defaultHidden = new Set(params.defaultHiddenColumns ?? []);

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return defaultHidden;

      return new Set((JSON.parse(raw) as string[]).filter((f) => allColumnFieldNames.has(f)));
    } catch {
      return defaultHidden;
    }
  });

  const columnVisibilityModel = useMemo(
    () =>
      Object.fromEntries(
        visibleColumns.map((col) => [col.field, !hiddenFieldNames.has(col.field)])
      ),
    [visibleColumns, hiddenFieldNames]
  );

  const onColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const newHiddenFields = new Set<string>();

      // v8 keys the model by field name. A missing key means visible (including "Show all" → {}).
      visibleColumns.forEach((col) => {
        if (model[col.field] === false) {
          newHiddenFields.add(col.field);
        }
      });

      // Preserve hidden state for columns absent from visibleColumns (e.g. state-filtered ones).
      // Guard by allColumnFieldNames to prevent stale/invalid values from propagating.
      hiddenFieldNames.forEach((field) => {
        if (allColumnFieldNames.has(field) && !visibleColumns.some((col) => col.field === field)) {
          newHiddenFields.add(field);
        }
      });

      setHiddenFieldNames(newHiddenFields);
      localStorage.setItem(storageKey, JSON.stringify([...newHiddenFields]));
    },
    [storageKey, allColumnFieldNames, visibleColumns, hiddenFieldNames]
  );

  return { columnVisibilityModel, onColumnVisibilityModelChange };
}
